import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'

import cx from 'classnames'

import { deleteComment } from 'api'
import { Comment } from 'modules/accountActivity/domain'
import { handleDeletion, initialCommentsVisible, parseCommentTree } from 'utils/comments'
import { segmentTrack, TrackType } from 'utils/tracking'

import CommentBody from './CommentBody'
import CommentComposer from './CommentComposer'

import styles from './styles.module.css'

interface CommentsProps {
  insightId: number
  initialComments: Comment[]
  onCommentAdded: () => void
  onCommentRemoved: (newNumComments: number) => void
  autofocusComposer: boolean
}

const NUM_COMMENTS_TO_EXPAND = 3

const LoadMoreButton: React.FC<{
  onClick: () => void
  className?: string
  isReplies?: boolean
}> = ({ onClick, className, isReplies = false }) => {
  return (
    <div className={cx(styles.loadMoreButton, className)} onClick={onClick}>
      {`View previous ${isReplies ? 'replies' : 'comments'}`}
    </div>
  )
}

const CommentReplies: React.FC<{
  insightId: number
  parentComment: Comment
  numRepliesInitiallyVisible: number
  shouldShowComposer: boolean
  onReplyClicked: () => void
  onCommentCreated: (comment: Comment) => void
  onCommentDeleted: (comment: Comment) => void
}> = ({
  insightId,
  parentComment,
  numRepliesInitiallyVisible,
  shouldShowComposer,
  onReplyClicked,
  onCommentCreated,
  onCommentDeleted
}) => {
  const [numRepliesVisible, setNumRepliesVisible] = useState<number>(numRepliesInitiallyVisible)

  const onReplyCreated = useCallback(
    (comment: Comment) => {
      setNumRepliesVisible(numRepliesVisible + 1)
      onCommentCreated(comment)
    },
    [onCommentCreated, numRepliesVisible]
  )

  const onReplyDeleted = useCallback(
    (comment: Comment) => {
      setNumRepliesVisible(numRepliesVisible - 1)
      onCommentDeleted(comment)
    },
    [onCommentDeleted, numRepliesVisible]
  )

  return (
    <div className={styles.commentReplies}>
      {numRepliesVisible < (parentComment.children?.length || 0) && (
        <LoadMoreButton
          className={styles.loadRepliesButton}
          onClick={() => setNumRepliesVisible(numRepliesVisible + NUM_COMMENTS_TO_EXPAND)}
          isReplies
        />
      )}
      {numRepliesVisible > 0 &&
        parentComment.children?.slice(-numRepliesVisible).map(reply => (
          <div key={reply.commentId} className={styles.replyContainer}>
            <CommentBody
              comment={reply}
              replyAction={onReplyClicked}
              onCommentDeleted={onReplyDeleted}
            />
          </div>
        ))}
      {shouldShowComposer && (
        <CommentComposer
          insightId={insightId}
          onCommentCreated={onReplyCreated}
          parentCommentId={parentComment.commentId}
          className={styles.replyComposer}
        />
      )}
    </div>
  )
}

const Comments = React.forwardRef<any, CommentsProps>(
  ({ insightId, initialComments, onCommentAdded, onCommentRemoved, autofocusComposer }, ref) => {
    const mainComposerRef = useRef<any>(null)
    const [commentList, setCommentList] = useState<Comment[]>(initialComments)
    const commentTree = useMemo(() => parseCommentTree(commentList), [commentList])
    const [replyingToId, setReplyingToId] = useState<string>()
    const [numVisibleComments, setNumVisibleComments] = useState<number>(
      initialCommentsVisible(commentTree)
    )

    const onCommentCreated = useCallback(
      (comment: Comment) => {
        setCommentList([...commentList, comment])
        if (!comment.parentCommentId) {
          setNumVisibleComments(numVisibleComments + 1)
        }
        onCommentAdded()

        segmentTrack(TrackType.COMMENT_CREATED, { insightId, comment })
      },
      [commentList, insightId, numVisibleComments, onCommentAdded]
    )

    const onCommentDeleted = useCallback(
      (comment: Comment) => {
        deleteComment(comment.resourceId, comment.commentId).then(() => {
          const prevNumComments = commentList.length
          const newList = handleDeletion(commentList, comment)
          setCommentList(newList)

          if (
            !comment.parentCommentId &&
            (prevNumComments - newList.length > 0 ||
              newList.find(c => c.commentId === comment.commentId)?.deleted)
          ) {
            setNumVisibleComments(numVisibleComments - 1)
          }
          onCommentRemoved(newList.filter(c => !c.deleted).length)

          segmentTrack(TrackType.COMMENT_DELETED, { insightId, comment })
        })
      },
      [commentList, insightId, numVisibleComments, onCommentRemoved]
    )

    const loadMoreComments = useCallback(
      () => setNumVisibleComments(numVisibleComments + NUM_COMMENTS_TO_EXPAND),
      [numVisibleComments]
    )

    useImperativeHandle(ref, () => ({
      focusMainComposer() {
        if (mainComposerRef?.current) {
          mainComposerRef.current.focus()
        }
      }
    }))

    return (
      <div className={styles.commentsContainer}>
        <div className={styles.commentList}>
          {numVisibleComments < commentTree.length && <LoadMoreButton onClick={loadMoreComments} />}
          {numVisibleComments > 0 &&
            commentTree.slice(-numVisibleComments).map((comment, index) => (
              <div key={comment.commentId} className={styles.commentContainer}>
                <CommentBody
                  comment={comment}
                  replyAction={() => setReplyingToId(comment.commentId)}
                  onCommentDeleted={onCommentDeleted}
                />
                <CommentReplies
                  insightId={insightId}
                  parentComment={comment}
                  numRepliesInitiallyVisible={index === numVisibleComments - 1 ? 2 : 1}
                  shouldShowComposer={replyingToId === comment.commentId}
                  onReplyClicked={() => setReplyingToId(comment.commentId)}
                  onCommentCreated={onCommentCreated}
                  onCommentDeleted={onCommentDeleted}
                />
              </div>
            ))}
        </div>
        <CommentComposer
          ref={mainComposerRef}
          insightId={insightId}
          onCommentCreated={onCommentCreated}
          autofocus={autofocusComposer}
        />
      </div>
    )
  }
)

export default Comments
